import { useTrans } from "@/utils/useTrans";
import Image from "next/image";
import { highlightHtml } from "@/components/tactsuit/tactglove-dk3/highlightHtml";

type UseCase = {
  title: string;
  description: string;
  imageSrc: string;
};

const UseCaseCard = ({ title, description, imageSrc }: UseCase) => (
  <div className="flex w-full shrink-0 flex-col items-start gap-6 laptop:gap-5">
    <div className="relative aspect-[280/210] w-full overflow-hidden rounded-2xl border border-coolgray-20 bg-coolgray-10 laptop:aspect-auto laptop:h-[348px] laptop:border-none">
      <Image src={imageSrc} alt={title} fill className="object-cover" />
    </div>
    <div className="flex w-full flex-col gap-3">
      <h3 className="text-[24px] font-400 leading-8 text-coolgray-90">
        {title}
      </h3>
      <p className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60">
        {description}
      </p>
    </div>
  </div>
);

export default function Dk3UseCases() {
  const { t } = useTrans(["tactsuit"]);

  const cases: UseCase[] = [
    {
      title: t("Enterprise, Medical & Defense Training"),
      description: t(
        "Provide realistic touch feedback for industrial maintenance, medical rehearsals, and tactical drills.",
      ),
      imageSrc: "/images/tactsuit/tactglove-dk3/usecase-enterprise.jpg",
    },
    {
      title: t("Advanced Simulation"),
      description: t(
        "Enhance driving and flight simulators with authentic feedback. Feel the vibration of the engine, the texture of the road, and the subtle rumble of the chassis.",
      ),
      imageSrc: "/images/tactsuit/tactglove-dk3/usecase-simulation.jpg",
    },
    {
      title: t("Social VR & HCI Research"),
      description: t(
        "From a firm handshake to a high-five. TactGlove DK3 brings digital interaction into real-life contact—serving as a bridge for social VR and a research-grade platform for HCI labs.",
      ),
      imageSrc: "/images/tactsuit/tactglove-dk3/usecase-social-vr.jpg",
    },
  ];

  return (
    <section className="w-full bg-white">
      <div className="mx-auto flex w-full max-w-screen-max flex-col gap-6 laptop:gap-10">
        {/* Header */}
        <header className="mx-auto flex max-w-[708px] flex-col items-center gap-6 px-4 text-center laptop:gap-4 laptop:px-8">
          <h2 className="text-2xl font-400 leading-8 text-coolgray-90 laptop:text-[32px] laptop:leading-10">
            {t("Where TactGlove works.")}
          </h2>
          <p
            className="text-base font-400 leading-[22px] tracking-[0.5px] text-coolgray-60"
            dangerouslySetInnerHTML={{
              __html: highlightHtml(
                t(
                  "Research, training, education — wherever finger-level touch matters. Bundles and institutional pricing available.",
                ),
              ),
            }}
          />
        </header>

        {/* 모바일: native scroll-snap horizontal carousel (3 cards).
            scroll-pl-4 로 각 카드 snap 위치가 viewport 좌측 16px 에 고정. */}
        <div className="snap-x snap-mandatory scroll-pl-4 overflow-x-auto pl-4 pr-4 [scrollbar-width:none] laptop:hidden [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-4">
            {cases.map((c) => (
              <div key={c.title} className="w-[85vw] shrink-0 snap-start">
                <UseCaseCard {...c} />
              </div>
            ))}
          </div>
        </div>

        {/* 데스크톱: 3-col flex */}
        <div className="hidden gap-6 px-8 laptop:flex">
          {cases.map((c) => (
            <div key={c.title} className="flex-1">
              <UseCaseCard {...c} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
